// Types pour le monitoring API
export interface ApiMetric {
  endpoint: string;
  method: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  status?: number;
  success: boolean;
  error?: string;
  cached: boolean;
}

export interface ApiMonitoringOptions {
  enabled: boolean;
  sampleRate: number; // Pourcentage (0-100) des requêtes à monitorer
  logToConsole: boolean;
  sendToAnalytics: boolean;
}

// Configuration par défaut
const DEFAULT_OPTIONS: ApiMonitoringOptions = {
  enabled: import.meta.env.DEV || import.meta.env.VITE_API_MONITORING === 'true',
  sampleRate: 100, // Par défaut, on monitore 100% des requêtes en dev
  logToConsole: import.meta.env.DEV,
  sendToAnalytics: import.meta.env.PROD,
};

class ApiMonitor {
  private options: ApiMonitoringOptions;
  private metrics: ApiMetric[] = [];
  private readonly MAX_METRICS = 100; // Limiter le stockage local

  constructor(options: Partial<ApiMonitoringOptions> = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  // Démarrer le monitoring d'une requête
  startRequest(endpoint: string, method: string, cached = false): string {
    // Vérifier si on doit échantillonner cette requête
    if (!this.shouldMonitor()) {
      return '';
    }

    const requestId = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    const metric: ApiMetric = {
      endpoint,
      method,
      startTime: performance.now(),
      success: false,
      cached
    };

    // Stocker temporairement la métrique
    this.metrics.push(metric);
    if (this.metrics.length > this.MAX_METRICS) {
      this.metrics.shift(); // Enlever la plus ancienne
    }

    return requestId;
  }

  // Finaliser le monitoring d'une requête
  endRequest(requestId: string, status: number, success: boolean, error?: string): void {
    if (!requestId) return;

    const metric = this.metrics.find(m => m.endpoint + m.startTime === requestId);
    if (!metric) return;

    metric.endTime = performance.now();
    metric.duration = metric.endTime - metric.startTime;
    metric.status = status;
    metric.success = success;
    metric.error = error;

    // Traiter la métrique
    this.processMetric(metric);
  }

  // Traitement d'une métrique
  private processMetric(metric: ApiMetric): void {
    // Logger dans la console en développement
    if (this.options.logToConsole) {
      const durationMs = metric.duration?.toFixed(2);
      const statusColor = metric.success ? '\x1b[32m' : '\x1b[31m'; // Vert ou rouge
      const cacheStatus = metric.cached ? '[CACHE] ' : '';
      
      console.log(
        `${cacheStatus}API ${metric.method} ${statusColor}${metric.status}\x1b[0m ${metric.endpoint} (${durationMs}ms)${
          metric.error ? ` - Erreur: ${metric.error}` : ''
        }`
      );
    }

    // Envoyer à un système d'analytics en production
    if (this.options.sendToAnalytics) {
      // À implémenter selon votre système d'analytics
      this.sendToAnalytics(metric);
    }
  }

  // Déterminer si la requête doit être monitorée (échantillonnage)
  private shouldMonitor(): boolean {
    if (!this.options.enabled) return false;
    return Math.random() * 100 <= this.options.sampleRate;
  }

  // Envoyer les données à un service d'analytics
  private sendToAnalytics(metric: ApiMetric): void {
    // Implémentation à venir - peut envoyer à Google Analytics, Mixpanel, etc.
    // Par exemple:
    /*
    if (window.gtag) {
      window.gtag('event', 'api_request', {
        event_category: 'api',
        event_label: metric.endpoint,
        value: metric.duration,
        method: metric.method,
        status: metric.status,
        success: metric.success ? 1 : 0
      });
    }
    */
  }

  // Obtenir des statistiques sur les appels API
  getStats() {
    const totalRequests = this.metrics.length;
    const successRequests = this.metrics.filter(m => m.success).length;
    const failedRequests = this.metrics.filter(m => !m.success).length;
    const cachedRequests = this.metrics.filter(m => m.cached).length;
    
    const avgDuration = this.metrics
      .filter(m => m.duration !== undefined)
      .reduce((sum, m) => sum + (m.duration || 0), 0) / totalRequests;
    
    return {
      totalRequests,
      successRequests,
      failedRequests,
      cachedRequests,
      successRate: totalRequests > 0 ? (successRequests / totalRequests) * 100 : 0,
      avgDurationMs: avgDuration || 0,
      metrics: this.metrics
    };
  }
}

// Exporter une instance unique
export const apiMonitor = new ApiMonitor();
export default apiMonitor;
